import React, { useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { getDevicePositions } from '../lib/adapters';
import { useI18n } from '../contexts/I18nContext';
import { useRelativeTime } from '../lib/time';
import { MapPin, Crosshair } from '@phosphor-icons/react';

const WINDOWS = [
  { key: 'last', hours: 720, track: false },
  { key: '1h', hours: 1, track: true },
  { key: '24h', hours: 24, track: true },
];

// Coloured circular pin as an inline SVG divIcon — avoids Leaflet's default
// marker PNGs (which break under webpack) and lets us tint SOS/fall/current.
function pin(color, size = 16, ring = 2) {
  return L.divIcon({
    className: 'e2-pin',
    html: `<span style="display:block;width:${size}px;height:${size}px;border-radius:9999px;background:${color};border:${ring}px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4)"></span>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}
// Current position: on-brand terracotta, larger "you-are-here" pin with a
// thick white ring so it stays distinct from the smaller incident dots.
const ICON_CURRENT = pin('#D64533', 22, 4);
// SOS incident: compact terracotta circle labelled "SOS".
const ICON_SOS = L.divIcon({
  className: 'e2-sos',
  html: `<span style="display:flex;align-items:center;justify-content:center;width:24px;height:24px;border-radius:9999px;background:#D64533;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4);color:#fff;font-size:8px;font-weight:800;letter-spacing:.4px">SOS</span>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});
const ICON_FALL = pin('#b4843f', 14, 2);

function Recenter({ points }) {
  const map = useMap();
  const sig = points.map((p) => p.join(',')).join('|');
  useEffect(() => {
    if (!points.length) return undefined;
    // Defer to the next tick and disable animation — fitting mid zoom-animation
    // throws Leaflet's "_leaflet_pos" error. sig keeps this stable across renders.
    const id = setTimeout(() => {
      try {
        if (points.length === 1) {
          map.setView(points[0], 16, { animate: false });
        } else {
          map.fitBounds(L.latLngBounds(points), { padding: [40, 40], maxZoom: 17, animate: false });
        }
      } catch (e) { /* map not ready */ }
    }, 0);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);
  return null;
}

export function DeviceLocationMap({ deviceId, testid = 'device-location-map' }) {
  const { t } = useI18n();
  const rel = useRelativeTime();
  const [win, setWin] = useState('last');
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const active = WINDOWS.find((w) => w.key === win) || WINDOWS[0];

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getDevicePositions(deviceId, active.hours)
      .then((p) => { if (alive) setPositions(p); })
      .catch(() => { if (alive) setPositions([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [deviceId, active.hours]);

  const current = positions.length ? positions[positions.length - 1] : null;
  const events = active.track ? positions.filter((p) => p.event_type === 'sos' || p.event_type === 'fall') : [];
  // Points the map fits to: the whole trail for 1h/24h, just the current fix
  // for "Last position" — so switching auto-zooms to reveal the full trail.
  const points = active.track
    ? positions.map((p) => [p.lat, p.lng])
    : (current ? [[current.lat, current.lng]] : []);

  // Age-faded track (1h / 24h only): each segment's opacity drops with how old
  // its newer end is relative to the latest fix. Fade span = the selected
  // window, so older positions gradually vanish (1h view fades over ~an hour).
  const segments = useMemo(() => {
    if (!active.track || positions.length < 2) return [];
    const latest = new Date(positions[positions.length - 1].at).getTime();
    const fadeMs = Math.max(1, active.hours) * 3600 * 1000;
    const segs = [];
    for (let i = 1; i < positions.length; i++) {
      const a = positions[i - 1];
      const b = positions[i];
      const f = 1 - (latest - new Date(b.at).getTime()) / fadeMs;
      if (f <= 0.03) continue; // too old — drop the line entirely
      segs.push({ id: i, pts: [[a.lat, a.lng], [b.lat, b.lng]], opacity: Math.min(0.9, 0.12 + f * 0.78) });
    }
    return segs;
  }, [positions, active.track, active.hours]);

  return (
    <div className="bg-white rounded-2xl border border-stone_line shadow-soft overflow-hidden" data-testid={testid}>
      <div className="flex items-center justify-between gap-3 flex-wrap px-6 py-4 border-b border-stone_line">
        <h2 className="font-heading text-xl text-ink flex items-center gap-2">
          <MapPin size={20} weight="duotone" className="text-sage-600" />
          {t('device_location')}
        </h2>
        <div className="flex gap-1 bg-bg-alt rounded-lg p-1" data-testid="location-window-tabs">
          {WINDOWS.map((w) => (
            <button
              key={w.key}
              onClick={() => setWin(w.key)}
              data-testid={`location-window-${w.key}`}
              className={`px-3 py-1 rounded-md text-xs font-semibold transition-colors ${
                win === w.key ? 'bg-sage text-white' : 'text-ink-soft hover:bg-white'
              }`}
            >
              {t(`device_track_${w.key}`)}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="h-[360px] grid place-items-center text-ink-muted">{t('loading')}</div>
      ) : !current ? (
        <div className="h-[360px] grid place-items-center text-center px-6" data-testid="location-empty">
          <div>
            <Crosshair size={32} className="text-ink-muted mx-auto mb-2" />
            <p className="text-sm text-ink-soft">{t('device_no_location')}</p>
          </div>
        </div>
      ) : (
        <>
          <div className="px-6 py-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm border-b border-stone_line bg-bg-alt/40">
            <span className="text-ink">
              <span className="text-ink-muted">{t('device_current_location')}:</span>{' '}
              <span className="font-mono">{current.lat.toFixed(5)}, {current.lng.toFixed(5)}</span>
            </span>
            {current.source && (
              <span className="text-ink-soft">
                <span className="text-ink-muted">{t('device_location_source')}:</span> {current.source.toUpperCase()}
              </span>
            )}
            <span className="text-ink-soft">
              <span className="text-ink-muted">{t('device_location_updated')}:</span> {rel(current.at)}
            </span>
          </div>
          <MapContainer
            center={[current.lat, current.lng]}
            zoom={16}
            scrollWheelZoom={false}
            style={{ height: '360px', width: '100%' }}
          >
            <TileLayer
              attribution='&copy; OpenStreetMap contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {segments.map((s) => (
              <Polyline key={s.id} positions={s.pts} pathOptions={{ color: '#D64533', weight: 4, opacity: s.opacity }} />
            ))}
            {events.map((e, i) => (
              <Marker key={`ev-${i}`} position={[e.lat, e.lng]} icon={e.event_type === 'sos' ? ICON_SOS : ICON_FALL}>
                <Popup>
                  <strong>{e.event_type === 'sos' ? t('map_sos') : t('map_fall')}</strong>
                  <br />{rel(e.at)}
                </Popup>
              </Marker>
            ))}
            <Marker position={[current.lat, current.lng]} icon={ICON_CURRENT}>
              <Popup>
                <strong>{t('map_current')}</strong>
                <br />{rel(current.at)}
              </Popup>
            </Marker>
            <Recenter points={points} />
          </MapContainer>
        </>
      )}
    </div>
  );
}
